import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FaFileAlt, 
  FaServer, 
  FaFolder, 
  FaDownload, 
  FaExclamationTriangle,
  FaLightbulb,
  FaInfoCircle 
} from 'react-icons/fa';
import FileExplorerModal from './fileexplorer';
import { getCookie } from './auth/cookies';
import MarkdownRenderer from './markdown';

function ChallengeFilesPage() {
    const [challengeFiles, setChallengeFiles] = useState([]);
    const [showExplorer, setShowExplorer] = useState(false);
    const [challengeContent, setChallengeContent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fileCount, setFileCount] = useState(0);

    // Fetch challenge files and content from the API
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            
            try {
                // Fetch both challenge files and content in parallel
                const [filesResponse, contentResponse] = await Promise.all([
                    axios.get('/api/get_challenge_files/', { 
                        headers: { 'X-CSRFToken': getCookie('csrftoken') } 
                    }),
                    axios.get('/api/load_challenge_content/', { 
                        headers: { 'X-CSRFToken': getCookie('csrftoken') } 
                    })
                ]);
                
                setChallengeFiles(filesResponse.data.files);
                setChallengeContent(contentResponse.data.content);
                setFileCount(filesResponse.data.files.length);
                
            } catch (err) {
                console.error('Error fetching challenge data:', err);
                setError(err.response?.data?.error || err.message || 'An unknown error occurred');
                toast.error(`Error loading challenge data: ${err.response?.data?.error || err.message}`);
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchData();
    }, []);

    const handleOpenExplorer = () => {
        if (challengeFiles.length === 0) {
            toast.warning('No challenge files available to explore.');
            return;
        }
        setShowExplorer(true);
    };

    return (
        <div className="bg-white dark:bg-gray-900 min-h-screen text-gray-900 dark:text-gray-200 pt-20">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
                {/* Header Section */}
                <header className="mb-12 text-center">
                    <h1 className="text-3xl md:text-4xl font-bold text-secondary-dark dark:text-white mb-4">
                        Challenge Files & Data
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                        Access all the necessary files and information for the hackathon challenge.
                    </p>
                </header>

                {isLoading ? (
                    /* Loading State */
                    <div className="flex justify-center items-center py-16">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
                        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading challenge data...</span>
                    </div>
                ) : error ? (
                    /* Error State */
                    <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-6 rounded-md mb-8">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <FaExclamationTriangle className="h-6 w-6 text-red-500" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-red-800 dark:text-red-300">
                                    Error Loading Challenge Data
                                </h3>
                                <p className="mt-2 text-red-700 dark:text-red-400">{error}</p>
                                <p className="mt-3">
                                    <button 
                                        onClick={() => window.location.reload()} 
                                        className="px-4 py-2 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-md hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
                                    >
                                        Try Again
                                    </button>
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Challenge Description Section */}
                        <section className="mb-12">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-secondary-dark p-4 flex items-center">
                                    <FaInfoCircle className="text-white mr-2" />
                                    <h2 className="text-lg md:text-xl font-semibold text-white">
                                        Challenge Description
                                    </h2>
                                </div>
                                <div className="p-6">
                                    {challengeContent ? (
                                        <div className="prose dark:prose-invert max-w-none">
                                            <MarkdownRenderer markdown={challengeContent} />
                                        </div>
                                    ) : (
                                        <p className="text-gray-600 dark:text-gray-400 italic">
                                            No challenge description available.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* File Explorer Section */}
                        <section className="mb-12">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-secondary-dark p-4 flex items-center justify-between">
                                    <div className="flex items-center">
                                        <FaFolder className="text-white mr-2" />
                                        <h2 className="text-lg md:text-xl font-semibold text-white">
                                            Challenge Files
                                        </h2>
                                    </div>
                                    <span className="text-sm text-white bg-secondary px-2 py-1 rounded-full">
                                        {fileCount} Files
                                    </span>
                                </div>

                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md border-l-4 border-blue-500">
                                            <div className="flex">
                                                <FaLightbulb className="flex-shrink-0 h-5 w-5 text-blue-500" />
                                                <div className="ml-3">
                                                    <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                                                        File Access
                                                    </h3>
                                                    <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                                                        Use the file explorer to browse and download challenge files. All necessary datasets and resources are included.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md border-l-4 border-yellow-500">
                                            <div className="flex">
                                                <FaServer className="flex-shrink-0 h-5 w-5 text-yellow-500" />
                                                <div className="ml-3">
                                                    <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                                                        Submission Requirements
                                                    </h3>
                                                    <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-400">
                                                        Remember to submit your solution according to the challenge guidelines. Pay attention to the required output format.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                                        <button
                                            onClick={handleOpenExplorer}
                                            className="w-full md:w-auto px-6 py-3 bg-secondary hover:bg-secondary-dark text-white rounded-md shadow-md transition duration-200 flex items-center justify-center"
                                            disabled={challengeFiles.length === 0}
                                        >
                                            <FaFolder className="mr-2" />
                                            {challengeFiles.length === 0 ? 'No Files Available' : 'Browse Challenge Files'}
                                        </button>
                                        
                                        {challengeFiles.length > 0 && (
                                            <div className="text-gray-500 dark:text-gray-400 text-sm">
                                                {fileCount} file{fileCount !== 1 ? 's' : ''} available
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        
                    </>
                )}

                {/* File Explorer Modal */}
                {showExplorer && (
                    <FileExplorerModal files={challengeFiles} onClose={() => setShowExplorer(false)} />
                )}
            </div>
        </div>
    );
}

export default ChallengeFilesPage;